<script setup>
import { ref, onMounted, onBeforeUnmount, watch, nextTick, computed } from 'vue';

const props = defineProps({
  column: {
    type: Object,
    required: true
  },
  items: {
    type: Array,
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  },
  hasMoreToLoad: {
    type: Boolean,
    default: false
  },
  columnTotal: {
    type: Number,
    default: 0
  },
  loadingText: {
    type: String,
    default: "Carregando..."
  },
  loadingMoreText: {
    type: String,
    default: "Carregando mais..."
  },
  emptyText: {
    type: String,
    default: "Nenhum item neste status"
  },
  endText: {
    type: String,
    default: "Fim da lista"
  }
});

const emit = defineEmits(['load-more']);

const sentinel = ref(null);
const observer = ref(null);
const containerRef = ref(null);
const isLoadingMore = ref(false);
const loadingDebounceTimeout = ref(null);

// Debounced load more function to prevent multiple rapid calls
const debouncedLoadMore = () => {
  if (loadingDebounceTimeout.value) {
    clearTimeout(loadingDebounceTimeout.value);
  }
  
  if (isLoadingMore.value || props.loading) return;
  
  isLoadingMore.value = true;
  
  // Use a short delay to prevent multiple rapid calls
  loadingDebounceTimeout.value = setTimeout(() => {
    emit('load-more', props.column.key);
    
    // Reset loading state after a moment to allow for another load
    setTimeout(() => {
      isLoadingMore.value = false;
    }, 500);
  }, 100);
};

// Create and attach observer with optimized settings
const setupObserver = () => {
  // Cleanup previous observer if exists
  if (observer.value) {
    observer.value.disconnect();
  }
  
  // Get the scrollable container
  const scrollContainer = containerRef.value?.$el || containerRef.value;
  
  // Initialize the intersection observer with more optimized settings
  if ('IntersectionObserver' in window && sentinel.value && scrollContainer) {
    observer.value = new IntersectionObserver((entries) => {
      // If the sentinel is intersecting and we have more to load
      if (entries[0].isIntersecting && 
          props.hasMoreToLoad && 
          !props.loading && 
          !isLoadingMore.value) {
        debouncedLoadMore();
      }
    }, {
      root: scrollContainer, 
      rootMargin: '2000px 0px', 
      threshold: 0.1
    });
    
    // Start observing the sentinel
    observer.value.observe(sentinel.value);
  }
};

// Watch for changes in the items list to reattach observer
watch(
  () => props.items,
  (newItems, oldItems) => {
    nextTick(() => {
      setupObserver();
      
      // Reset loading flag when items have changed
      if (newItems.length !== oldItems?.length) {
        isLoadingMore.value = false;
      }
    });
  },
  { deep: false }
);

// Watch for loading state changes
watch(
  () => props.loading,
  (isLoading) => {
    if (!isLoading) {
      // Reset loading more flag when main loading is done
      isLoadingMore.value = false;
    }
  }
);

onMounted(() => {
  nextTick(() => {
    setupObserver();    
  });
});

onBeforeUnmount(() => {
  if (observer.value) {
    observer.value.disconnect();
  }
  
  if (loadingDebounceTimeout.value) {
    clearTimeout(loadingDebounceTimeout.value);
  }
});
</script>

<template>
  <div class="kanban-column" v-show="column.visible">
    <v-card class="h-100">
      <v-card-title class="d-flex justify-space-between pa-3 text-black" :style="`background-color: ${column.color}`">
        <span>{{ column.title }}</span>
        <v-chip class="ml-2" size="small" variant="outlined" color="black">
          {{ columnTotal || items.length }}
        </v-chip>
      </v-card-title>

      <v-card-text class="pa-2 kanban-cards" ref="containerRef">
        <!-- Loading state at the top if data is loading and no items -->
        <div v-if="loading && !items.length" class="pa-2 text-center">
          <v-progress-circular indeterminate color="primary" size="24"></v-progress-circular>
          <div class="text-body-2 mt-2">{{ loadingText }}</div>
        </div>
          
        <!-- Empty state when no items -->
        <div v-else-if="!items.length" class="text-center py-8 text-medium-emphasis">
          {{ emptyText }}
        </div>
          
        <!-- Items with infinite scroll -->
        <template v-else>
          <div class="items-container">
            <slot :items="items"></slot>
          </div>
            
          <!-- Loading more indicator - more subtle with fade transition -->
          <div v-if="loading || isLoadingMore" class="pa-2 text-center loading-indicator">
            <v-progress-circular indeterminate color="primary" size="20" :width="2"></v-progress-circular>
            <div class="text-caption mt-1">{{ loadingMoreText }}</div>
          </div>
          
          <!-- Sentinel element positioned higher up -->
          <div 
            v-if="hasMoreToLoad && !loading" 
            class="load-more-sentinel" 
            ref="sentinel">
          </div>
            
          <!-- End of list indicator -->
          <div v-if="!hasMoreToLoad && !loading && columnTotal > 0" 
               class="text-center py-2 text-medium-emphasis text-caption end-indicator">
            {{ endText }}
          </div>
        </template>
      </v-card-text>
    </v-card>
  </div>
</template>

<style scoped>
.kanban-column {
  flex: 0 0 320px;
  max-width: 320px;
  min-width: 320px;
  height: calc(100vh - 205px);
  margin-left: 1px;
}

.kanban-cards {
  height: calc(100vh - 250px);
  overflow-y: auto;
  overflow-x: hidden;
  scroll-behavior: smooth;
  will-change: transform; /* Hint for browser to optimize animations */
  -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
}

/* Container for all items with hardware acceleration */
.items-container {
  transform: translateZ(0); /* Force GPU acceleration */
}

/* Sentinel element for infinite scroll */
.load-more-sentinel {
  height: 200px; /* Taller sentinel means earlier loading */
  width: 100%;
  background-color: transparent;
  position: relative;
}

/* Loading indicator with subtle appearance */
.loading-indicator {
  opacity: 0.7;
  height: 40px;
  transition: opacity 0.3s ease;
}

/* End indicator with fade-in effect */
.end-indicator {
  opacity: 0.6;
  padding: 5px 0;
  transition: opacity 0.5s ease;
}

/* Scrollbars with smoother appearance */
.kanban-cards::-webkit-scrollbar {
  width: 6px;
}

.kanban-cards::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.15);
  border-radius: 6px;
}

.kanban-cards::-webkit-scrollbar-track {
  background-color: rgba(0, 0, 0, 0.03);
}

@media (max-width: 768px) {
  .kanban-column {
    flex: 0 0 280px;
    max-width: 280px;
    min-width: 280px;
  }
}

/* Hide the debug button in production */
.load-more-debug {
  opacity: 0.7;
  display: none; /* Hide the debug button */
}
</style>
